import React from "react";
import { CalendarCheck } from "lucide-react";
import { BudgetState, BudgetType, TIMEZONES, rupees } from "./types";
import { Field, RadioCard, SectionHeader, SelectBox, StepFooter, inputCls } from "./ui";

const StepBudget: React.FC<{
  budget: BudgetState;
  update: (patch: Partial<BudgetState>) => void;
  onBack: () => void;
  onNext: () => void;
  onSaveDraft: () => void;
  savingDraft: boolean;
  nextDisabled?: boolean;
}> = ({ budget, update, onBack, onNext, onSaveDraft, savingDraft, nextDisabled }) => {
  const daily = budget.budgetType === "Daily Budget";
  const pick = (t: BudgetType) => update({ budgetType: t });

  return (
    <div>
      <SectionHeader title="Budget and Schedule" subtitle="Setup budget and schedule the broadcast" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <RadioCard selected={daily} onClick={() => pick("Daily Budget")} title="Daily Budget">
          Spend up to this amount each day.
        </RadioCard>
        <RadioCard selected={!daily} onClick={() => pick("Lifetime Budget")} title="Lifetime Budget">
          Set one total amount for the entire broadcast.
        </RadioCard>
      </div>

      <div className="mt-5 md:max-w-[50%] md:pr-2.5">
        <Field label={budget.budgetType}>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-gray-900 dark:text-gray-100">₹</span>
            <input
              type="number"
              min={0}
              value={budget.amount}
              onChange={(e) => update({ amount: Number(e.target.value) })}
              className={`${inputCls} pl-8 border-primary-700 dark:border-primary-500`}
            />
          </div>
        </Field>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1.5">
          {daily ? `Estimated spend: ${rupees(budget.amount * 30)} over 30 days` : `Total spend: ${rupees(budget.amount)} for the whole broadcast`}
        </p>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
        <Field label="Ad Start Time">
          <div className="relative">
            <input
              type="datetime-local"
              value={budget.startAt}
              onChange={(e) => update({ startAt: e.target.value })}
              className={`${inputCls} pr-12`}
            />
            <CalendarCheck size={22} className="absolute right-4 top-1/2 -translate-y-1/2 text-primary-700 pointer-events-none bg-white dark:bg-gray-900" />
          </div>
        </Field>
        <Field label="Time Zone">
          <SelectBox value={budget.timezone} onChange={(v) => update({ timezone: v })} options={TIMEZONES} />
        </Field>
      </div>

      <label className="mt-4 inline-flex items-center gap-2.5 text-base text-gray-800 dark:text-gray-200 cursor-pointer">
        <input
          type="checkbox"
          checked={budget.hasEnd}
          onChange={(e) => update({ hasEnd: e.target.checked })}
          className="w-5 h-5 rounded border-gray-300 text-primary-700 focus:ring-primary-500"
        />
        Set End Time
      </label>
      {budget.hasEnd && (
        <div className="mt-3 md:max-w-[50%] md:pr-2.5">
          <Field label="Ad End Time">
            <input type="datetime-local" value={budget.endAt} onChange={(e) => update({ endAt: e.target.value })} className={inputCls} />
          </Field>
        </div>
      )}

      <StepFooter onBack={onBack} onNext={onNext} nextLabel="Preview Broadcast" onSaveDraft={onSaveDraft} savingDraft={savingDraft} nextDisabled={nextDisabled} />
    </div>
  );
};

export default StepBudget;
